// Fleet → Boot server releases: the Clavian Boot server's own software, updated by hand at each venue.
//
//  - **Releases.** The registered boot server releases, newest first: the version, the channel it was cut for, when, the
//    MSI's size, and the channels it is current on. "Make current" opens a centred dialog to choose the channel; making
//    one current offers it to the boot servers on that channel, which download and verify it and then wait for the venue
//    to choose Update now — nothing installs by itself.
//  - **Channels.** What each channel offers now, with Stop offering.
//  - **Register release.** A dialog taking the signed manifest the boot server release tool printed; the server checks
//    the signature (`422`) and refuses a version registered twice (`409`).
//
// `hq:releases` gates every route here, as it does the station releases on the same page.

import type { BootServerRelease, BootServerReleaseChannel, BootServerReleaseChannelState } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, Button, Dialog, DialogClose, EmptyState, Field, InfoTip, SelectField, Skeleton, Table, useToast, type TableColumn } from '@clavian/ui-web';
import { useState, type FormEvent, type ReactNode } from 'react';

import { problemCode, problemDetail, PROBLEM } from '../../api/problems';
import { useProblem } from '../../api/use-problem';
import { LoadFailure } from '../../shell/LoadFailure';
import { Icon } from '../../shell/nav-icons';
import { Notice, Section } from '../../ui';
import { useBootServerReleases, useRegisterBootServerRelease, useSetBootServerReleaseChannel } from './queries';

const COMPACT_JWS = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
const CHANNELS: readonly BootServerReleaseChannel[] = ['stable', 'beta'];

export function BootServerReleases(): ReactNode {
  const { t } = useTranslation('hq');
  const format = useVenueFormatter();
  const list = useBootServerReleases();
  const [registering, setRegistering] = useState(false);
  const [making, setMaking] = useState<BootServerRelease | undefined>(undefined);

  const columns: readonly TableColumn<BootServerRelease>[] = [
    { key: 'version', header: t('releases.boot.column.version'), cell: (release) => <span className="console-num console-releases-version">{release.version}</span> },
    { key: 'channel', header: t('releases.boot.column.channel'), cell: (release) => <Badge tone={release.channel === 'stable' ? 'neutral' : 'accent'}>{t(`releases.channel.${release.channel}`)}</Badge> },
    {
      key: 'released',
      header: t('releases.boot.column.released'),
      cell: (release) => <span className="console-num">{format.dateTime(release.manifest.releasedAt, { date: 'medium', time: 'short' })}</span>,
    },
    {
      key: 'size',
      header: t('releases.boot.column.size'),
      numeric: true,
      cell: (release) => <span className="console-num">{format.number(release.manifest.msi.size / 1_048_576, { style: 'unit', unit: 'megabyte', maximumFractionDigits: 1 })}</span>,
    },
    {
      key: 'current',
      header: t('releases.boot.column.current'),
      cell: (release) =>
        release.currentOn.length === 0 ? (
          <span className="console-muted">{t('releases.boot.notCurrent')}</span>
        ) : (
          <span className="console-releases-current">
            {release.currentOn.map((channel) => (
              <Badge key={channel} tone="ok">
                {t(`releases.channel.${channel}`)}
              </Badge>
            ))}
          </span>
        ),
    },
    {
      key: 'actions',
      header: t('releases.boot.column.actions'),
      cell: (release) =>
        release.currentOn.length === CHANNELS.length ? null : (
          <Button variant="ghost" size="sm" icon={<Icon name="launch" />} label={t('releases.boot.makeCurrentLabel', { version: release.version })} onClick={() => setMaking(release)}>
            <span>{t('releases.boot.makeCurrent')}</span>
          </Button>
        ),
    },
  ];

  let body: ReactNode;
  if (list.data === undefined) {
    body = list.isError ? <LoadFailure error={list.error} message={t('releases.boot.failed')} onRetry={() => void list.refetch()} /> : <Skeleton height="8rem" />;
  } else if (list.data.data.length === 0) {
    body = (
      <EmptyState
        size="compact"
        icon={<Icon name="upload" />}
        title={t('releases.boot.empty')}
        action={
          <Button variant="primary" icon={<Icon name="plus" />} onClick={() => setRegistering(true)}>
            {t('releases.boot.register.action')}
          </Button>
        }
      />
    );
  } else {
    body = (
      <>
        <Channels channels={list.data.channels} />
        <Table caption={t('releases.boot.caption')} captionHidden columns={columns} rows={list.data.data} rowKey={(release) => release.version} />
      </>
    );
  }

  return (
    <Section
      title={t('releases.boot.title')}
      body={list.data !== undefined && list.data.data.length > 0 ? 'rows' : 'padded'}
      actions={
        <>
          <InfoTip>{t('releases.boot.intro')}</InfoTip>
          <Button variant="primary" icon={<Icon name="plus" />} onClick={() => setRegistering(true)}>
            {t('releases.boot.register.action')}
          </Button>
        </>
      }
    >
      {body}
      <RegisterDialog open={registering} onOpenChange={setRegistering} />
      <MakeCurrentDialog release={making} onClose={() => setMaking(undefined)} />
    </Section>
  );
}

/** What each channel offers now, with Stop offering. */
function Channels({ channels }: { readonly channels: readonly BootServerReleaseChannelState[] }) {
  const { t } = useTranslation('hq');
  const problems = useProblem();
  const { show } = useToast();
  const set = useSetBootServerReleaseChannel();
  return (
    <ul className="console-releases-boot-channels" aria-label={t('releases.boot.channelsLabel')}>
      {channels.map((state) => {
        const channel = t(`releases.channel.${state.channel}`);
        return (
          <li key={state.channel} className="console-releases-boot-channel">
            <span>
              {state.currentVersion === null
                ? t('releases.boot.channelNone', { channel })
                : t('releases.boot.channelCurrent', { channel, version: state.currentVersion })}
            </span>
            {state.currentVersion === null ? null : (
              <Button
                variant="ghost"
                size="sm"
                icon={<Icon name="stop" />}
                busy={set.isPending && set.variables?.channel === state.channel}
                label={t('releases.boot.stopLabel', { channel })}
                onClick={() =>
                  set.mutate(
                    { channel: state.channel, currentVersion: null },
                    {
                      onSuccess: () => show({ tone: 'ok', title: t('releases.boot.current.stopped', { channel }) }),
                      onError: (error) => show({ tone: 'danger', title: problems.text(error, { fallback: t('releases.boot.current.failed') }) }),
                    },
                  )
                }
              >
                <span>{t('releases.boot.stop')}</span>
              </Button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function MakeCurrentDialog({ release, onClose }: { readonly release: BootServerRelease | undefined; readonly onClose: () => void }) {
  const { t } = useTranslation('hq');
  const problems = useProblem();
  const { show } = useToast();
  const set = useSetBootServerReleaseChannel();
  const choices = CHANNELS.filter((channel) => release?.currentOn.includes(channel) !== true);
  const [picked, setPicked] = useState<BootServerReleaseChannel | undefined>(undefined);
  const channel = picked !== undefined && choices.includes(picked) ? picked : (choices[0] ?? 'stable');
  const version = release?.version ?? '';

  const close = () => {
    set.reset();
    setPicked(undefined);
    onClose();
  };
  const submit = () => {
    if (release === undefined || set.isPending) return;
    set.mutate(
      { channel, currentVersion: release.version },
      {
        onSuccess: () => {
          show({ tone: 'ok', title: t('releases.boot.current.done', { version, channel: t(`releases.channel.${channel}`) }) });
          close();
        },
      },
    );
  };

  return (
    <Dialog
      open={release !== undefined}
      onOpenChange={(next) => (next ? undefined : close())}
      size="sm"
      title={t('releases.boot.current.title', { version })}
      description={t('releases.boot.current.description')}
      footer={
        <>
          <DialogClose asChild>
            <Button>{t('releases.boot.current.cancel')}</Button>
          </DialogClose>
          <Button variant="primary" busy={set.isPending} onClick={submit}>
            {t('releases.boot.current.submit')}
          </Button>
        </>
      }
    >
      <SelectField
        label={t('releases.boot.current.channel')}
        options={choices.map((value) => ({ value, label: t(`releases.channel.${value}`) }))}
        value={channel}
        onValueChange={(value) => setPicked(value as BootServerReleaseChannel)}
      />
      {set.isError ? (
        <Notice tone="danger" role="alert">
          {problemCode(set.error) === PROBLEM.unprocessable ? t('releases.boot.current.unregistered') : problems.text(set.error, { fallback: t('releases.boot.current.failed') })}
        </Notice>
      ) : null}
    </Dialog>
  );
}

function RegisterDialog({ open, onOpenChange }: { readonly open: boolean; readonly onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation('hq');
  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="md" dirty="auto" title={t('releases.boot.register.title')} description={t('releases.boot.register.description')}>
      {open ? <RegisterForm onDone={() => onOpenChange(false)} /> : null}
    </Dialog>
  );
}

function RegisterForm({ onDone }: { readonly onDone: () => void }) {
  const { t } = useTranslation('hq');
  const problems = useProblem();
  const { show } = useToast();
  const register = useRegisterBootServerRelease();
  const [text, setText] = useState('');
  const [refused, setRefused] = useState<string | undefined>(undefined);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (register.isPending) return;
    const manifest = text.trim();
    const local = manifest === '' ? t('releases.boot.register.required') : COMPACT_JWS.test(manifest) ? undefined : t('releases.boot.register.malformed');
    setRefused(local);
    if (local !== undefined) return;
    register.mutate(manifest, {
      onSuccess: (release) => {
        show({ tone: 'ok', title: t('releases.boot.register.done', { version: release.version }) });
        onDone();
      },
    });
  };

  let serverError: { readonly title: string; readonly detail: string | undefined } | undefined;
  if (register.isError) {
    const detail = problemDetail(register.error);
    const code = problemCode(register.error);
    serverError =
      code === PROBLEM.conflict
        ? { title: t('releases.boot.register.conflict'), detail }
        : code === PROBLEM.unprocessable
          ? { title: t('releases.boot.register.unprocessable'), detail }
          : { title: problems.text(register.error, { fallback: t('releases.boot.register.failedGeneric') }), detail };
  }

  return (
    <form className="console-releases-register" noValidate onSubmit={submit}>
      <Field label={t('releases.boot.register.manifest')} description={t('releases.boot.register.manifestHint')} error={refused} required>
        {(control) => (
          <textarea
            id={control.id}
            aria-describedby={control['aria-describedby']}
            aria-invalid={control['aria-invalid']}
            required
            className="clav-input console-releases-register__manifest"
            rows={8}
            spellCheck={false}
            autoComplete="off"
            value={text}
            onChange={(event) => setText(event.target.value)}
          />
        )}
      </Field>
      {serverError === undefined ? null : (
        <Notice tone="danger" role="alert" title={serverError.title}>
          {serverError.detail === undefined ? null : <p>{serverError.detail}</p>}
        </Notice>
      )}
      <div className="console-releases-register__actions">
        <Button type="button" onClick={onDone}>
          {t('releases.boot.register.cancel')}
        </Button>
        <Button type="submit" variant="primary" icon={<Icon name="upload" />} busy={register.isPending}>
          {t('releases.boot.register.submit')}
        </Button>
      </div>
    </form>
  );
}
